import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormGroup, NonNullableFormBuilder } from '@angular/forms';
import {
  incomeFormRules,
  sharedFormValidator,
} from '@credit-acceptance/shareable-utilities';
import { FullStory } from '@fullstory/browser';
import { adobeAnalytics } from 'adobe-analytics';
import isEqual from 'lodash/isEqual';
import moment from 'moment';
import { takeUntil } from 'rxjs';

import { error_sign, PREQUAL_JSON } from '../../assets/constants';
import { PrequalService } from '../services/prequal.service';
import { RuntimeConfigService } from '../services/runtime-config';
import { Unsubscribe } from '../shared/classes/unsubscribe.class';
import { FS_EVENTS } from '../shared/constants/fsevents';
import { dateOfBirth } from '../shared/state/local-cache';
import { applyServerFieldErrorsToGroup } from '../summary/caps-validation-field-errors';
import { CrossComponentDateValidationService } from '../validators/cross-component-date-validation.service';
import { SiblingValidationService } from '../validators/sibling-validation.service';

declare const FS: typeof FullStory;

type TIncomeType = 'primary' | 'secondary';

const formShape: {
  incomeType: string;
  biWeeklyPay: string;
  employerName?: string | undefined | null;
  hireDate: string;
  employerPhone?: string | undefined | null;
} = {
  incomeType: '',
  biWeeklyPay: '',
  employerName: '',
  hireDate: '',
  employerPhone: '',
};

@Component({
  selector: 'capq-income',
  templateUrl: './income.component.html',
  styleUrls: ['./income.component.scss'],
  standalone: false,
})
export class IncomeComponent extends Unsubscribe implements OnInit, OnDestroy {
  parseBiWeeklyPay = (value: string) => value.replace(',', '');

  sanitizePhoneNumber(value: string, key: keyof typeof incomeFormRules) {
    return key === 'employerPhone' ? value.replace(/-/g, '') : value;
  }

  private shouldRemoveControls = (incomeType: string) =>
    ['Fixed Income (Net)', 'Unemployed', ''].includes(incomeType);

  @Output() activeStep: EventEmitter<number> = new EventEmitter<number>();
  @Input() adobeScriptLoaded = false;
  incomeForm = this.formBuilder.group(formShape);
  secIncomeForm = this.formBuilder.group(formShape);
  excessIncomeFlag = false;
  incomeOptionsList: any;
  dobMisMatchError = false;
  futureDateError = false;
  secDobMisMatchError = false;
  secFutureDateError = false;
  showSecondaryIncomeForm = false;
  profanityFlag = false;
  biWeeklyAmountCounter = 0;
  biweeklyIncome: any;
  monthlyincome: any;
  annualIncome: any;
  secbiweeklyIncome: any;
  secmonthlyincome: any;
  secannualIncome: any;
  icnIconURL = 'assets/images/info2.png';
  retriveApplication = true;
  personalData: any;
  biWeekly: string | undefined;
  biWeeklyPay: string | undefined;
  grossIncomeTotal: any;
  grossIncomeErrorMessage: any;
  isIncomeViewEventCalled = false;
  exclamation = error_sign;
  globalText: any;
  formFieldData: any;
  belowMinimum = false;
  combinedIncome = 0;
  pageViewFired = false;
  formdirtyCounter = 0;
  formStartEventEmitted = false;
  formIncomplete = {
    primary: true,
    secondary: true,
  };

  constructor(
    private readonly formBuilder: NonNullableFormBuilder,
    private readonly prequalService: PrequalService,
    private siblingValidationService: SiblingValidationService,
    private cdRef: ChangeDetectorRef,
    private readonly runtimeConfigService: RuntimeConfigService,
    private crossComponentDateValidation: CrossComponentDateValidationService
  ) {
    super();

    this.crossComponentDateValidation.registerResidencyValidator(
      this.crossValidateDates.bind(this)
    );
  }

  crossValidateDates() {
    this.incomeForm.controls.hireDate.value &&
      this.incomeForm.controls.hireDate.valid &&
      this.validateHireDate(this.incomeForm.controls.hireDate.value, 'primary');
    this.secIncomeForm.controls.hireDate.value &&
      this.secIncomeForm.controls.hireDate.valid &&
      this.validateHireDate(
        this.secIncomeForm.controls.hireDate.value,
        'secondary'
      );
  }

  biWeeklyFSEvent(countValue: any) {
    if (!(typeof FS === 'undefined')) {
      FS.event(FS_EVENTS.BI_WEEKlY_UPDATE_COUNT, {
        count: countValue,
      });
    }
  }

  ngOnInit(): void {
    this.prequalService.serverFieldErrors$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((errors) => {
        applyServerFieldErrorsToGroup(errors, 'income', (name) =>
          this.incomeForm.get(name)
        );
        applyServerFieldErrorsToGroup(errors, 'income.secondary', (name) =>
          this.secIncomeForm.get(name)
        );
        this.cdRef.detectChanges();
      });

    this.incomeForm.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.formdirtyCounter = this.formdirtyCounter + 1;

        if (
          this.formdirtyCounter > 2 &&
          this.adobeScriptLoaded &&
          !this.pageViewFired
        ) {
          adobeAnalytics.emitEvent('cac-page-view', 'income', {
            form_design: 'v 1.0',
            section_name: 'income',
            form_submission_id: '',
            dealer_list: '',
            form_submission_status: '',
            event_type: 'cac-page-view',
          });
          this.pageViewFired = true;
        }
        const { employerName, employerPhone, ...initState } = formShape;
        const {
          employerName: formEmployerName,
          employerPhone: formEmployerPhone,
          ...formValue
        } = this.incomeForm.getRawValue();
        if (
          this.adobeScriptLoaded &&
          !isEqual(initState, formValue) &&
          !this.formStartEventEmitted
        ) {
          this.formStartEventEmitted = true;
          adobeAnalytics.emitFormStart('income');
        }
      });

    this.siblingValidationService.validate$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        const primaryErrors = this.preserveErrors(
          this.incomeForm.controls,
          'primary'
        );
        let secondaryErrors = {};
        if (this.showSecondaryIncomeForm) {
          secondaryErrors = this.preserveErrors(
            this.secIncomeForm.controls,
            'secondary'
          );
        }
        this.validateBiWeeklyIncome();
        this.excessIncome();
        this.cdRef.detectChanges();
        this.incomeForm.markAllAsTouched();

        for (const key of Object.keys(this.incomeForm.controls)) {
          if (key === 'biWeeklyPay') {
            const value = this.parseBiWeeklyPay(
              this.incomeForm.get(key)?.value || ''
            );
            this.validate(
              value,
              key as keyof typeof incomeFormRules,
              'primary'
            );
          } else {
            if (key === 'hireDate') {
              this.validateHireDate(this.incomeForm.get(key)?.value, 'primary');
            }
            this.validate(
              this.incomeForm.get(key)?.value,
              key as keyof typeof incomeFormRules,
              'primary'
            );
          }
        }

        if (this.showSecondaryIncomeForm) {
          this.secIncomeForm.markAllAsTouched();
          for (const key of Object.keys(this.secIncomeForm.controls)) {
            if (key === 'biWeeklyPay') {
              const value = this.parseBiWeeklyPay(
                this.secIncomeForm.get(key)?.value || ''
              );
              this.validate(
                value,
                key as keyof typeof incomeFormRules,
                'secondary'
              );
            } else {
              if (key === 'hireDate') {
                this.validateHireDate(
                  this.incomeForm.get(key)?.value,
                  'secondary'
                );
              }
              this.validate(
                this.secIncomeForm.get(key)?.value,
                key as keyof typeof incomeFormRules,
                'secondary'
              );
            }
          }
          this.restoreErrors(
            this.secIncomeForm.controls,
            secondaryErrors,
            'secondary'
          );
        }

        this.restoreErrors(this.incomeForm.controls, primaryErrors, 'primary');

        const isValid =
          this.incomeForm.valid &&
          this.secIncomeForm.valid &&
          !this.belowMinimum &&
          !this.excessIncomeFlag &&
          !this.dobMisMatchError &&
          !this.formIncomplete.primary;

        this.siblingValidationService.reportValidation(
          3,
          {
            primary: {
              ...this.incomeForm.getRawValue(),
              biWeeklyPay: this.parseBiWeeklyPay(
                this.incomeForm.controls.biWeeklyPay.value
              ),
            },
            secondary: this.showSecondaryIncomeForm
              ? {
                  ...this.secIncomeForm.getRawValue(),
                  biWeeklyPay: this.parseBiWeeklyPay(
                    this.secIncomeForm.controls.biWeeklyPay.value
                  ),
                }
              : undefined,
          },
          isValid
        );
      });
    this.globalText = PREQUAL_JSON;
    this.formFieldData = PREQUAL_JSON?.income?.formsFields;
    this.initForm();

    this.incomeOptionsList = this.formFieldData?.incomeType?.options || [];
  }

  initForm(): void {
    if (this.shouldRemoveControls(this.incomeForm.controls.incomeType.value)) {
      this.removeEmployerControls('primary');
    }

    this.incomeForm.controls.incomeType.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((selectedValue: string) => {
        if (this.shouldRemoveControls(selectedValue)) {
          this.removeEmployerControls('primary');
        } else {
          this.addEmployerControls('primary');
        }

        if (selectedValue !== 'Fixed Income (Net)') {
          this.prequalService.updatePrimaryIncomeFieldAdded(true);
        }
      });
    this.incomeForm.controls.biWeeklyPay.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((value: any) => {
        this.excessIncome();
        const numeric = parseFloat(value);
        if (isNaN(numeric) || numeric <= 0) {
          this.incomeForm.controls.biWeeklyPay.setValue('', {
            emitEvent: false,
          });
        }
      });

    this.secIncomeForm.controls.biWeeklyPay.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.excessIncome();
      });

    // secondary form settings
    if (
      this.shouldRemoveControls(this.secIncomeForm.controls.incomeType.value)
    ) {
      this.removeEmployerControls('secondary');
    }
    this.secIncomeForm.controls.incomeType.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((selectedValue: string) => {
        if (this.shouldRemoveControls(selectedValue)) {
          this.removeEmployerControls('secondary');
        } else {
          this.addEmployerControls('secondary');
        }
      });

    this.retrieveData();

    this.incomeForm.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.formChangeListener('primary');
      });
    this.secIncomeForm.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.formChangeListener('secondary');
      });
  }

  formChangeListener(type: TIncomeType) {
    const form = type === 'primary' ? this.incomeForm : this.secIncomeForm;

    const entries = Object.entries(form.value) as [
      keyof typeof incomeFormRules,
      string
    ][];
    const missingValue = entries.find(
      ([key, value]) => !value && incomeFormRules[key].required
    );

    if (missingValue) {
      this.formIncomplete[type as keyof typeof this.formIncomplete] = true;
    } else {
      for (const [key, value] of entries) {
        this.validate(value, key, type, true);
      }
    }
  }

  removeTrailingSpaces(event: any, primary: boolean) {
    if (primary) {
      this.incomeForm.controls['employerName']?.setValue(
        event.target.value.trim()
      );
    } else {
      this.secIncomeForm.controls['employerName']?.setValue(
        event.target.value.trim()
      );
    }
  }

  parseInputAndValidate(event: FocusEvent, key: any, form: TIncomeType) {
    const { value } = event.target as HTMLInputElement;
    let valueToValidate =
      key === 'biWeeklyPay' ? this.parseBiWeeklyPay(value) : value;
    valueToValidate = this.sanitizePhoneNumber(valueToValidate, key);
    this.validate(valueToValidate, key, form);
    this.validateBiWeeklyIncome();
    this.excessIncome();
  }

  validateBiWeeklyIncome() {
    const { MIN_BIWEEKLY_INCOME } = this.runtimeConfigService.prequalConfig;
    if (
      this.incomeForm.controls.biWeeklyPay.touched ||
      this.secIncomeForm.controls.biWeeklyPay.touched
    ) {
      const primaryBiWeeklyPay =
        this.incomeForm.controls.biWeeklyPay.value.replace(',', '') || '0';
      const secondaryBiWeeklyPay =
        this.secIncomeForm.controls.biWeeklyPay.value.replace(',', '') || '0';
      this.combinedIncome =
        parseFloat(primaryBiWeeklyPay) + parseFloat(secondaryBiWeeklyPay);
      if (this.combinedIncome < MIN_BIWEEKLY_INCOME) {
        this.belowMinimum = true;
      } else {
        this.belowMinimum = false;
      }
    }
  }

  validate(
    value: string,
    key: keyof typeof incomeFormRules,
    form: TIncomeType,
    softCheck?: boolean
  ) {
    const forms = {
      primary: this.incomeForm,
      secondary: this.secIncomeForm,
    };
    const { errors } = sharedFormValidator('incomeFormRules', key, value);
    if (errors && errors.length) {
      const errorObject: Record<string, string> = {};
      for (const err of errors) {
        errorObject[err.rule] = err.message;
        if (err.rule === 'isNotProfanity') {
          this.profanityFlag = true;
        } else {
          this.profanityFlag = false;
        }
      }
      softCheck
        ? (this.formIncomplete[form as keyof typeof this.formIncomplete] = true)
        : forms[form].get(key)?.setErrors(errorObject);
    } else {
      softCheck
        ? (this.formIncomplete[form as keyof typeof this.formIncomplete] =
            false)
        : forms[form].get(key)?.setErrors(null);
    }

    // Setting below to use value as untouched is coming as true although the input is touched and value exists
    if (
      !forms[form].controls.employerName?.value &&
      forms[form].controls.employerName?.touched
    ) {
      this.formIncomplete[form as keyof typeof this.formIncomplete] = true;
    }

    if (
      !forms[form].controls.employerPhone?.value &&
      forms[form].controls.employerName?.touched
    ) {
      this.formIncomplete[form as keyof typeof this.formIncomplete] = true;
    }
  }

  parseAndValidateDate(event: FocusEvent, type: TIncomeType = 'primary') {
    const { value } = event.target as HTMLInputElement;
    this.validateHireDate(value, type);
  }

  checkAlphaNumPattern(event: Event) {
    const regex = new RegExp('^[a-zA-Z0-9\\s]+$');
    const { key } = event as KeyboardEvent;
    if (key && !regex.test(key)) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  validateHireDate(selectedValue: any, type: TIncomeType = 'primary') {
    const errorMap = {
      primary: {
        dobMisMatchError: this.dobMisMatchError,
        futureDateError: this.futureDateError,
      },
      secondary: {
        dobMisMatchError: this.secDobMisMatchError,
        futureDateError: this.secFutureDateError,
      },
    };
    if (
      selectedValue &&
      moment(selectedValue, 'MM/DD/YYYY', true).isValid() &&
      this.incomeForm.controls.hireDate.valid
    ) {
      const date = selectedValue; // Your date
      const format = 'MM/DD/YYYY'; // Your date format
      const resultFormat = 'years'; // Result format (years, months, days)
      const birthDate = dateOfBirth();
      if (birthDate) {
        const diff = moment(birthDate, format).diff(
          moment(date, format),
          resultFormat,
          true
        );
        const diffFromToday = moment().diff(
          moment(date, format),
          resultFormat,
          true
        );
        if (diff > 0) {
          errorMap[type].dobMisMatchError = true;
          errorMap[type].futureDateError = false;
        } else if (diffFromToday < 0) {
          errorMap[type].futureDateError = true;
          errorMap[type].dobMisMatchError = false;
        } else {
          errorMap[type].dobMisMatchError = false;
          errorMap[type].futureDateError = false;
        }
      }
    } else {
      errorMap[type].dobMisMatchError = false;
      errorMap[type].futureDateError = false;
    }

    if (type == 'primary') {
      this.dobMisMatchError = errorMap.primary.dobMisMatchError;
      this.futureDateError = errorMap.primary.futureDateError;
    } else if (type == 'secondary') {
      this.secDobMisMatchError = errorMap.secondary.dobMisMatchError;
      this.secFutureDateError = errorMap.secondary.futureDateError;
    }
  }

  retrieveData() {
    const incomeData = this.incomeForm.getRawValue();
    const secondaryIncomeData = this.secIncomeForm.getRawValue();

    if (incomeData && incomeData.incomeType) {
      this.incomeForm.markAllAsTouched();
      this.incomeForm.setValue(incomeData);

      const income = incomeData['biWeeklyPay'].replace(',', '');

      this.biweeklyIncome = Number(income).toLocaleString();
      this.monthlyincome = ((Number(income) * 26) / 12)
        .toFixed(0)
        .replace(/\d(?=(\d{3})+\.)/g, '$&,');
      this.monthlyincome = Number(this.monthlyincome).toLocaleString();
      this.annualIncome = (Number(income) * 26)
        .toFixed(0)
        .replace(/\d(?=(\d{3})+\.)/g, '$&,');
      this.annualIncome = Number(this.annualIncome).toLocaleString();
    }
    if (secondaryIncomeData && secondaryIncomeData.incomeType) {
      this.showSecondaryIncomeForm = true;
      this.secIncomeForm.markAllAsTouched();
      this.secIncomeForm.setValue(secondaryIncomeData);
      const income = secondaryIncomeData['biWeeklyPay'].replace(',', '');

      this.secbiweeklyIncome = Number(income).toLocaleString();
      this.secmonthlyincome = ((Number(income) * 26) / 12)
        .toFixed(0)
        .replace(/\d(?=(\d{3})+\.)/g, '$&,');
      this.secmonthlyincome = Number(this.secmonthlyincome).toLocaleString();
      this.secannualIncome = (Number(income) * 26)
        .toFixed(0)
        .replace(/\d(?=(\d{3})+\.)/g, '$&,');
      this.secannualIncome = Number(this.secannualIncome).toLocaleString();
    }
  }

  preserveErrors(controls: any, type: string) {
    const errors: Record<typeof type, Record<keyof typeof controls, any>> = {
      [type]: {},
    };
    for (const control of Object.keys(controls)) {
      if (controls[control].errors) {
        errors[type][control] = controls[control].errors;
      }
    }
    return errors;
  }

  restoreErrors(controls: any, errors: any, type: string) {
    for (const control of Object.keys(controls)) {
      if (control in errors[type]) {
        controls[control].setErrors(errors[type][control]);
      }
    }
  }

  addEmployerControls(type: string) {
    const form = type === 'primary' ? this.incomeForm : this.secIncomeForm;
    // adding/removing controls from a form also seems to setErrors to null. Method below preserves the errors
    const errors = this.preserveErrors(form.controls, type);
    form.addControl('employerName', this.formBuilder.control(''));
    form.addControl('employerPhone', this.formBuilder.control(''));
    // and then restores them after the form controls were added/removed
    this.restoreErrors(form.controls, errors, type);
  }

  removeEmployerControls(type: string) {
    const form = type === 'primary' ? this.incomeForm : this.secIncomeForm;
    // adding/removing controls from a form also seems to setErrors to null. Method below preserves the errors
    const errors = this.preserveErrors(form.controls, type);
    // const controls = ['employerName', 'employerPhone'];
    (form as FormGroup).removeControl('employerName');
    (form as FormGroup).removeControl('employerPhone');
    // and then restores them after the form controls were added/removed
    this.restoreErrors(form.controls, errors, type);
  }

  transformAmount(event: Event, formType = 'primary') {
    const val = (event.target as HTMLInputElement).value.replace(/,/g, '');
    const { name } = event.target as HTMLInputElement;
    const transformedAmount = Number(val)
      .toFixed(0)
      .replace(/\d(?=(\d{3})+\.)/g, '$&,');
    if (transformedAmount !== '0.00') {
      if (formType === 'primary') {
        this.incomeForm
          .get(name)
          ?.setValue(Number(transformedAmount).toLocaleString());
        this.biweeklyIncome = Number(transformedAmount).toLocaleString();
        this.monthlyincome = ((Number(transformedAmount) * 26) / 12)
          .toFixed(0)
          .replace(/\d(?=(\d{3})+\.)/g, '$&,');
        this.monthlyincome = Number(this.monthlyincome).toLocaleString();
        this.annualIncome = (Number(transformedAmount) * 26)
          .toFixed(0)
          .replace(/\d(?=(\d{3})+\.)/g, '$&,');
        this.annualIncome = Number(this.annualIncome).toLocaleString();
        this.biWeeklyFSEvent(this.biWeeklyAmountCounter + 1);
      } else {
        this.secIncomeForm
          .get(name)
          ?.setValue(Number(transformedAmount).toLocaleString());
        this.secbiweeklyIncome = Number(transformedAmount).toLocaleString();
        this.secmonthlyincome = ((Number(transformedAmount) * 26) / 12)
          .toFixed(0)
          .replace(/\d(?=(\d{3})+\.)/g, '$&,');
        this.secmonthlyincome = Number(this.secmonthlyincome).toLocaleString();
        this.secannualIncome = (Number(transformedAmount) * 26)
          .toFixed(0)
          .replace(/\d(?=(\d{3})+\.)/g, '$&,');
        this.secannualIncome = Number(this.secannualIncome).toLocaleString();
      }
    }
  }

  navigateBack() {
    this.activeStep.emit(2);
  }

  showSecondaryForm(value: any) {
    if (!value) {
      this.showSecondaryIncomeForm = value;
      for (const key of Object.keys(this.secIncomeForm.controls)) {
        if (key != 'incomeType') {
          this.secIncomeForm.get(key)?.patchValue('');
        }
        this.secIncomeForm.get(key)?.markAsPristine();
        this.secIncomeForm.get(key)?.markAsUntouched();
        this.secIncomeForm.get(key)?.setErrors(null);
      }
      this.secbiweeklyIncome = '';
      this.secmonthlyincome = '';
      this.secannualIncome = '';
      this.prequalService.setSecondaryIncomeData(null);
      // calc if below minimum
      this.validateBiWeeklyIncome();
    } else {
      if (this.showSecondaryIncomeForm && this.secIncomeForm.untouched) {
        this.showSecondaryForm(false);
      }
      this.showSecondaryIncomeForm = value;
      this.removeEmployerControls('secondary');
      this.secIncomeForm.controls.incomeType.setValue('');
      this.prequalService.updateSecondaryIncomeAdded(true);
      this.formIncomplete.secondary = true;
    }
  }

  trimOut(value: string) {
    return value.replace(/[^a-zA-Z0-9 ]*/g, '');
  }

  get f() {
    return this.incomeForm.controls;
  }

  get sf() {
    return this.secIncomeForm.controls;
  }

  excessIncome() {
    const { MAX_COMBINED_BIWEEKLY_INCOME } =
      this.runtimeConfigService.prequalConfig;
    const primaryBiWeeklyPay =
      +this.incomeForm.controls.biWeeklyPay?.value.replaceAll(',', '') || 0;
    const secondaryBiWeeklyPay =
      +this.secIncomeForm.controls.biWeeklyPay?.value.replaceAll(',', '') || 0;
    if (
      primaryBiWeeklyPay + secondaryBiWeeklyPay >
      MAX_COMBINED_BIWEEKLY_INCOME
    ) {
      this.excessIncomeFlag = true;
    } else {
      this.excessIncomeFlag = false;
    }
  }

  noIncome() {
    return (
      this.annualIncome === 0 ||
      (this.secannualIncome !== '' && this.secannualIncome === 0)
    );
  }
}
